import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  E_COUNTRY,
  E_DATE_FORMAT,
  E_FAVICONS,
  E_FOLDER_TYPES,
  E_FR_LOC,
  E_QPROPS,
  E_URL_PARTS,
  MONTH_NAMES_FR,
  STORAGE_KEYS,
} from '@shared/constants/enums.const';
import {
  IFoldersResponse,
  IFullFranchiseItem,
  IHome_FranchisesResponse,
  ILocalizationsItem,
  ITagCategoryItem,
} from '@shared/constants/models.const';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter } from 'rxjs';
import { SelectCategoryComponent } from '../pages/home/components/select-category/select-category.component';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class HelperService {
  changeFrClicked = false; // track try to change frLoc, reset in getData()/chose-page-type.ts

  loginEnabled = true;

  rootFolderId = 0;
  menuItems: IFoldersResponse[] = [];
  frItems: IHome_FranchisesResponse[] = [];
  currentFrItem!: IFullFranchiseItem;
  contentType: E_FOLDER_TYPES = E_FOLDER_TYPES.Home; // uses in Header to highlight current tab
  frLocArray: string[] = Object.values(E_FR_LOC);

  private currentFr: E_FR_LOC = E_FR_LOC.en;
  selectCategoryStickyState = false;
  $pageData = new BehaviorSubject<any>(null);

  currentFrLoc = signal<E_FR_LOC>(E_FR_LOC.en);


  private router: Router = inject(Router);
  $onReload = this.router.events
    .pipe(
      untilDestroyed(this),
      filter(event => event instanceof NavigationEnd),
    );

  private route: ActivatedRoute = inject(ActivatedRoute);
  private translate: TranslateService = inject(TranslateService);

  getUrlFromPageData(frLoc: E_FR_LOC): string {
    const locArr: ILocalizationsItem[] = this.$pageData?.value?.localizations || [];
    const locItem = locArr.find(item => item.route === frLoc);
    return locItem ? `${locItem?.route}/${locItem.sourceUrl}` : '';
  }

  getContentType(prop: any): string {
    return prop;
    // if (!prop || `${prop}` === '0') {
    //   return '127';
    // } else {
    //   return prop;
    // }
  }

  get frLoc(): E_FR_LOC {
    return this.currentFr;
  }


  set frLoc(data: E_FR_LOC) {
    this.currentFrLoc.set(data);
    this.currentFr = data;
  }

  get currentLang() {
    return this.translate.currentLang;
  }

  ifContains(str: string, checkArr: string[]): boolean {
    return checkArr.some(item => str.includes(item));
  }

  selectCategoryHandle(SelectCategory: SelectCategoryComponent) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const elementRect = entry.boundingClientRect;
        const rootRect = entry.rootBounds;
        this.selectCategoryStickyState = elementRect.bottom < rootRect!?.top;
        SelectCategory.clickOutside();
      });
    });
    observer.observe(SelectCategory.getElement());
  }


  getFormatedDate(date: string): string {
    const newDate = new Date(date);
    const formatter = new Intl.DateTimeFormat('en', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return formatter.format(newDate);
  }

  getFormatByFr(formatStr: string): string {
    const currentLang = this.currentLang;
    if (currentLang === E_FR_LOC.en) return formatStr;

    const formatsObj: any = {
      de: {
        [E_DATE_FORMAT.ddMMMyyyy]: 'dd.MM.yyyy ',
        [E_DATE_FORMAT.ddMMM_HHmm]: 'dd.MM HH:mm',
        [E_DATE_FORMAT.ddMMMyyyy_HHmm]: 'dd.MM.yyyy, HH:mm',
      },
      fr: {
        [E_DATE_FORMAT.ddMMMyyyy]: 'yyyy.MM.dd',
        [E_DATE_FORMAT.ddMMM_HHmm]: 'MM.dd HH:mm',
        [E_DATE_FORMAT.ddMMMyyyy_HHmm]: 'yyyy.MM.dd, HH:mm',
      },

    };


    return (formatsObj[currentLang] && formatsObj[currentLang][formatStr])
      ? formatsObj[currentLang][formatStr]
      : formatStr;
  }

  getOnlyDate(value: string, includeTime = false): string {
    let result = value?.split('T')[0];
    if (includeTime) {
      result = value?.split('.')[0];
    }
    return result || value;
  }


  LocalizedMonth(dateString: string) {
    const dateObj = new Date(this.getOnlyDate(dateString));
    const month = dateObj.getMonth();
    return MONTH_NAMES_FR[this.currentLang][month] || MONTH_NAMES_FR['en'][month];
  }


  formatDateWithLocalizedMonth(dateString: string, onlyDate = true, includeTime = false): string {

    const dateObj = new Date(onlyDate ? this.getOnlyDate(dateString, includeTime) : dateString);

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = dateObj.getMonth(); // 0-11
    const monthNumber = (month + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    const monthName = MONTH_NAMES_FR[this.currentLang][month] || MONTH_NAMES_FR['en'][month];

    let formattedDate = `${day} ${monthName} ${year}`;
    switch (this.currentLang) {
      case E_FR_LOC.de:
        formattedDate = `${day}.${monthNumber}.${year}`;
        break;
      case E_FR_LOC.fr:
        formattedDate = `${year}.${monthNumber}.${day}`;
        break;

    }
    if (includeTime) {
      formattedDate += `, ${hours}:${minutes}`;
    }
    return formattedDate;
  }


  enumArr<T extends Record<string, string | number>>(enumObj: T): T[keyof T][] {
    return Object.values(enumObj) as T[keyof T][];
  }

  navigateToRelated(contentType = 0, ID: number) {
    this.router.navigate([this.getUrl(`${E_FOLDER_TYPES.Related}/${ID}`)], {
      queryParams: {
        'content-type': contentType,
      },
    });
  }

  updateFavIco(): void {
    const favicon = document.getElementById('appIcon') as HTMLLinkElement;
    switch (this.currentFrItem?.country) {
      case E_COUNTRY.Canada:
        switch (this.frLoc) {
          case E_FR_LOC.caEn:
            favicon.href = E_FAVICONS.CanadaEng;
            break;
          case E_FR_LOC.caFr:
            favicon.href = E_FAVICONS.CanadaFrench;
            break;
          default:
            favicon.href = E_FAVICONS.Global;
        }
        break;
      case E_COUNTRY.UnitedKingdom:
        favicon.href = E_FAVICONS.UnitedKingdom;
        break;
      default:
        favicon.href = E_FAVICONS.Global;
    }
    this.setTranslation();

    const ogImage = document.getElementById('ogImage') as HTMLLinkElement;
    if (ogImage) {
      ogImage.setAttribute('content', 'https://iasplus.com/assets/cif-favicon.png');
    }

  }

  setTranslation(): void {
    switch (this.frLoc) {
      case E_FR_LOC.fr:
      case E_FR_LOC.caFr:
        this.translate.use((E_FR_LOC.fr) as string);
        break;
      case E_FR_LOC.de:
        this.translate.use((E_FR_LOC.de) as string);
        break;
      default:
        this.translate.use((E_FR_LOC.en) as string);
    }
  }


  goUrl(sourceUrl: string, ignoreFrLoc = false) {
    if (ignoreFrLoc) {
      this.router.navigate([sourceUrl]);
    } else {
      this.router.navigate([this.getUrl(sourceUrl)]);
    }

  }

  getUrl(url = '', addTags = false, returnObj = false): any {
    if (returnObj) {
      return {
        url: '/' + [this.frLoc, url].join('/'),
        props: addTags ? { 'tag-type': this.hasTags() } : {},
      };
    } else {
      const baseUrl = `/${this.frLoc}/${url}`.replace('(', '%28').replace(')', '%29');
      if (addTags && !!this.hasTags()) {
        return `${baseUrl}?${this.hasTags()?.map(item => `tag-type=${item}`).join('&')}`;
      }
      return baseUrl;
    }

  }

  getUrlArray(value = ''): string[] {
    const url = value || this.router.url;
    return url
      ? url.split('?')[0].split('/').filter(item => item !== '')
      : [];
  }

  getFrLocFromUrl(): string | null {
    const urlArr = this.getUrlArray();
    const frLocFromUrl = urlArr[E_URL_PARTS.frLoc];
    return this.frLocArray.includes(frLocFromUrl) ? frLocFromUrl : null;
  }

  getQueryParams() {
    return this.route.snapshot?.queryParams;
  }

  changeFrLoc(frLoc: string, withParams = true) {
    if (!this.frLocArray.includes(frLoc)) return;

    const urlArr = this.getUrlArray();
    const frLocFromUrl = this.getFrLocFromUrl();

    if (frLocFromUrl) {
      urlArr[E_URL_PARTS.frLoc] = frLoc;
    } else {
      urlArr.unshift(frLoc);
    }

    this.updateFavIco();

    let tagTypes: number[] = this.hasTags() || [];
    const currentTagsIds = this.getTagCategoryList().categoryList.map(item => item.value);
    const resetTags = tagTypes.some(tt => !currentTagsIds.includes(tt));

    if (withParams && !resetTags) {
      this.router.navigate([urlArr.join('/')], { queryParams: this.getQueryParams() });
    } else {
      this.router.navigate([urlArr.join('/')]);
    }


  }

  seeAll(type: E_FOLDER_TYPES): void {
    this.router.navigate([this.getUrlFromMenu(type)]);
  }

  getUrlFromMenu(type: E_FOLDER_TYPES): string {
    const menuItem = this.menuItems.find(item => item.type === type);
    if (menuItem) {
      return this.getUrl(menuItem.sourceUrl);
    } else {
      return this.getUrl(E_FOLDER_TYPES.Home);
    }
  }


  getTagCategoryList(): {categoryList: ITagCategoryItem[], allSelectedValue: number} {
    if (!this.currentFrItem?.searchableContentTagTypes?.length) {
      return {
        allSelectedValue: 0,
        categoryList: [],
      };
    }
    const categoryList = this.currentFrItem.searchableContentTagTypes.map(item => {
      return {
        value: item.id,
        DisplayName: item.name,
        checked: true,
      };
    });

    const allSelectedValue = categoryList.reduce((prev, curr) => (prev + curr.value), 0) + 1;

    categoryList.unshift({
      DisplayName: 'All',
      value: allSelectedValue,
      checked: true,
    });

    return {
      categoryList,
      allSelectedValue,
    };
  }

  convertLink(url: string, notHref = false): string {
    const frPosition = 0;
    const frArr = Object.values(E_FR_LOC);

    if (url) {
      const clearRoute = this.removeDomain(url, notHref)
        .replace('href="', '')
        .replace('\\', '/');

      let routeParts = clearRoute.split('/').filter(item => !!item);

      if (!frArr.includes(routeParts[frPosition] as E_FR_LOC)) {
        routeParts = [this.frLoc, ...routeParts];
      }

      if (!notHref) {
        routeParts = ['href="', ...routeParts];
      }
      url = routeParts.join('/');
    }

    return url;
  }

  removeDomain(url: string, notHref = false): string {
    if (notHref) {
      return url.replace(/^https?:\/\/[^\/]+/, '');
    }
    return url.replace(/^href="https?:\/\/[^\/]+/, '');
  }

  getAllCategoriesSum(): number {
    let allSelectedValue = 0;
    this.currentFrItem?.searchableContentTypes?.forEach((item) => {
      allSelectedValue += item.id;
    });
    return allSelectedValue;
  }

  onCategoryChanged(tagType: number[]): void {
    const queryParams = tagType
      ? {
        [E_QPROPS.tagType]: tagType,
        [E_QPROPS.skip]: 0,
      }
      : { [E_QPROPS.skip]: 0 };

    this.router.navigate([], {
      queryParamsHandling: 'merge',
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }


  lastUrlCache = '';

  saveForLastUrl(title = '') {
    const urlArr = this.getUrlArray();
    const skipUrlArr = ['search', 'tag-related', 'related', 'related-publications'];
    const isSkipUrl = skipUrlArr.some(item => urlArr.includes(item));
    if (isSkipUrl) return;

    const cashValue = urlArr.join('/') + JSON.stringify(this.getQueryParams()) + JSON.stringify(title);
    if (cashValue !== this.lastUrlCache) {
      this.lastUrlCache = cashValue;
      sessionStorage.setItem(STORAGE_KEYS.lastUrl, urlArr.join('/'));
      sessionStorage.setItem(STORAGE_KEYS.LastUrlParams, JSON.stringify(this.getQueryParams()));
      sessionStorage.setItem(STORAGE_KEYS.lastTitle, JSON.stringify(title));
    }

  }

  getLastTitle(): string {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEYS.lastTitle) || '');
  }

  getLastUrl(noFrLoc = false): string {
    const url = sessionStorage.getItem(STORAGE_KEYS.lastUrl) || '';
    const urlArr = this.getUrlArray(url);
    const isFrLocCorrect = this.frLocArray.includes(urlArr[E_URL_PARTS.frLoc]);
    if (isFrLocCorrect && noFrLoc) {
      urlArr.shift();
    }

    return urlArr.length ? urlArr.join('/') : url;
  }

  goLastUrl(): void {
    const url = sessionStorage.getItem(STORAGE_KEYS.lastUrl);
    const queryParams = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LastUrlParams) || '{}');
    this.router.navigate([url], {
      queryParams: queryParams,
    });
  }


  getProps(take5 = false) {
    let params = { ...this.route.snapshot.queryParams };
    const hasTags = this.hasTags();
    const tagsValue = this.getTagsValue();


    if (hasTags && tagsValue) {
      params = {
        ...params,
        ['tag-type']: this.getTagsValue(),
      };
    }

    if (!params['take'] && take5) {
      params['take'] = 5;
    }

    return params;
  }


  hasTags(ignoreStoredTags = false): number[] | null {

    if (this.isTagAllowed()) {
      if (!window.location.href.includes('?') && ignoreStoredTags) {
        this.clearTags();
        return null;
      }

      const queryParams = this.route.snapshot?.queryParams[E_QPROPS.tagType];
      const storageParams = JSON.parse(`${sessionStorage.getItem(STORAGE_KEYS.tags) || null}`);
      const tagParams = queryParams || storageParams;


      const searchableContentTagTypes = this.currentFrItem?.searchableContentTagTypes?.map(item => Number(item.id)) || [];

      if (tagParams) {
        let result = Array.isArray(tagParams)
          ? tagParams.map(item => Number(item))
          : [Number(tagParams)];

        if (searchableContentTagTypes.length) {
          result = result.filter(item => searchableContentTagTypes.includes(item));
        }

        sessionStorage.setItem(STORAGE_KEYS.tags, JSON.stringify(result));
        return result;
      }
      this.clearTags();
      return null;
    }
    return null;
  }


  getTagsValue(): number | null {
    let tagTypes: number[] = this.hasTags() || [];
    if (!tagTypes.length) {return null;}

    return tagTypes.reduce((prev, current) => {
      return prev + Number(current);
    }, 0) || this.getTagCategoryList().allSelectedValue;

  }

  isTagAllowed(): boolean {
    const urlArr = this.getUrlArray();

    // TODO Somehow rewrite that part, we need this based on BA API
    const pagesArr = [
      'home', 'news', 'nouvelles', 'tag-types', 'nachrichten', 'publications',
    ];

    return pagesArr.some(name => {
      return urlArr.includes(name);
    });
  }

  clearTags(): void {
    sessionStorage.removeItem(STORAGE_KEYS.tags);
  }


}


export function _CLEAR(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}
